import type {
  ConfigTreeNode,
  ConfigRecord,
  ConfigField,
} from '~/settings/components/ConfigurationsPage/types';
import type {
  ConfigurationTreeResponse,
  ConfigurationResponse,
  DefaultFieldMetadata,
  ConfigurationLevel,
} from '~/settings/components/ConfigurationsPage/helpers/configurationApi';
import type { Account } from '~/shared/utils/types';
import { MIN_SIZE_BYTES_DOUBLE_DISPLAY_FIELDS } from '~/settings/components/ConfigurationsPage/helpers/utils';
import { parseConfigValue, formatConfigValue } from '~/settings/utils/configValueParser';
import { nodeIdSeparator } from '~/settings/components/ConfigurationsPage/helpers/nodeId';

function generateNodeName(
  id: string,
  level: ConfigurationLevel,
  accountId?: string,
  accounts?: Account[]
): { name: string; isLoading: boolean } {
  if (level === 'org') {
    const account = accounts?.find((acc) => acc.accountId === id);
    if (account?.accountName) {
      return { name: account.accountName, isLoading: false };
    }
    return { name: 'Organization', isLoading: false };
  }

  if (level === 'account' && accountId) {
    if (!accounts) {
      return { name: '', isLoading: true };
    }

    const account = accounts.find((acc) => acc.accountId === accountId);
    if (account?.accountName) {
      return { name: account.accountName, isLoading: false };
    }

    return { name: '', isLoading: true };
  }

  if (level === 'cluster') {
    // For cluster level, use the cluster ID as the name
    return { name: id, isLoading: false };
  }

  if (level === 'instance') {
    return { name: id, isLoading: false };
  }

  if (level === 'node') {
    return { name: id, isLoading: false };
  }

  if (level === 'volume') {
    return { name: id, isLoading: false };
  }

  return {
    name: '',
    isLoading: false,
  };
}

export function transformTreeResponse(
  response: ConfigurationTreeResponse,
  accounts?: Account[],
  parentAccountId?: string,
  parentClusterId?: string,
  parentPath?: string
): ConfigTreeNode {
  const accountId =
    response.level === 'account' || response.level === 'org' ? response.id : parentAccountId;
  const clusterId = response.level === 'cluster' ? response.id : parentClusterId;
  const { name, isLoading } = generateNodeName(response.id, response.level, accountId, accounts);

  // Entity ids are only unique within their parent — the same cluster can exist in two
  // accounts — so the node id is qualified by its ancestors. The raw ids live on in
  // accountId/clusterId/instanceId below, which is what the API is given.
  const id = parentPath ? `${parentPath}${nodeIdSeparator}${response.id}` : response.id;

  const node: ConfigTreeNode = {
    id,
    name,
    type: response.level,
    level: response.level,
    clusterId,
    instanceId:
      response.level === 'instance' || response.level === 'node' || response.level === 'volume'
        ? response.id
        : undefined,
    accountId: accountId,
    isLoading,
    hasOverrides: response.hasOverrides,
  };

  if (response.children && response.children.length > 0) {
    const accountIdForChildren = response.level === 'account' ? response.id : accountId;
    const clusterIdForChildren = response.level === 'cluster' ? response.id : clusterId;
    node.children = response.children.map((child) =>
      transformTreeResponse(child, accounts, accountIdForChildren, clusterIdForChildren, id)
    );
  }

  return node;
}

/**
 * Transform API configuration responses to ConfigRecord
 * Uses metadata from /defaults endpoint (name, description, type, validValues)
 */
export function transformConfigurationResponse(
  responseWithOverrides: ConfigurationResponse,
  defaults: Record<string, DefaultFieldMetadata>,
  nodeId: string,
  nodeName: string,
  nodeType: ConfigTreeNode['type']
): ConfigRecord {
  const fields: ConfigField[] = [];

  const allKeys = new Set<string>();

  if (responseWithOverrides.configurationsWithOverrides) {
    Object.keys(responseWithOverrides.configurationsWithOverrides).forEach((key) =>
      allKeys.add(key)
    );
  }

  if (defaults) {
    Object.keys(defaults).forEach((key) => allKeys.add(key));
  }

  // Create fields from the combined data
  allKeys.forEach((key) => {
    const overrideData = responseWithOverrides.configurationsWithOverrides?.[key];
    const metadata = defaults[key];

    let defaultValue = metadata?.value ?? '';
    let currentValue = overrideData?.value ?? defaultValue;
    const isOverridden = overrideData?.valueOrigin === 'overridden';

    // For shrink.min_size_bytes and datafy.min_size_bytes: display 2× API value
    if (
      metadata?.type === 'size' &&
      (MIN_SIZE_BYTES_DOUBLE_DISPLAY_FIELDS as readonly string[]).includes(key)
    ) {
      const doubleSize = (value: string) => {
        const parsed = parseConfigValue(String(value), 'size');
        return formatConfigValue(parsed.value * 2, parsed.unit, 'size');
      };
      currentValue = doubleSize(currentValue);
      defaultValue = doubleSize(defaultValue);
    }

    // Extract unit from the value for size and duration types
    let unit: string | undefined;
    if (metadata?.type === 'size' || metadata?.type === 'duration') {
      const regex = /^(\d+(?:\.\d+)?)\s*([a-z]+)?$/i;
      const match = defaultValue.match(regex);
      if (match && match[2]) {
        unit = match[2].toLowerCase();
      } else {
        // Default units if not found in value
        unit = metadata?.type === 'size' ? 'gb' : 's';
      }
    }

    // Only show fields that are customer-facing in defaults metadata
    if (metadata?.customerFacing !== true) {
      return;
    }

    const fieldObject = {
      name: key,
      currentValue: String(currentValue),
      defaultValue: String(defaultValue),
      isOverridden,
      displayName: metadata?.name !== '' ? metadata?.name : undefined,
      description: metadata?.description !== '' ? metadata?.description : undefined,
      type: metadata?.type,
      validValues: metadata?.validValues,
      valueOrigin: overrideData?.valueOrigin,
      inheritedFrom: overrideData?.inheritedFrom,
      unit,
      minValue: metadata?.minValue,
      maxValue: metadata?.maxValue,
    };

    fields.push(fieldObject);
  });

  return {
    nodeId,
    nodeName,
    nodeType,
    fields,
    lastUpdated: responseWithOverrides.updated || '',
  };
}
